import { BAR_STYLES, CANDLE_FINISHES, CANDLE_STYLES, CENTERPIECE_STYLES, CHAIR_STYLES, CHUPPAH_STYLES, COCKTAIL_TABLE_STYLES, DANCE_FLOOR_STYLES, GLASS_SHAPES, LINEN_FINISHES, NAPKIN_FOLDS, LOUNGE_STYLES, CUTLERY_FINISHES, DECOR_FLOOR_FINISHES, DEFAULT_DECOR, GLASS_STYLES, MAX_FLOWER_COLORS, } from '../types/decor.js';
// ─── Colours ────────────────────────────────────────────────────────────────
const HEX = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;
/**
 * A colour as `#rrggbb`, lower case, from what somebody typed or pasted —
 * `#ABC`, `abc123`, ` #aabbcc ` — or null when it is not a colour.
 */
export function normalizeHexColor(value) {
    if (typeof value !== 'string')
        return null;
    const m = HEX.exec(value.trim());
    if (!m)
        return null;
    const digits = m[1].length === 3
        ? m[1]
            .split('')
            .map((c) => c + c)
            .join('')
        : m[1];
    return `#${digits.toLowerCase()}`;
}
export function hexToRgb(hex) {
    const n = normalizeHexColor(hex) ?? '#000000';
    return {
        r: parseInt(n.slice(1, 3), 16),
        g: parseInt(n.slice(3, 5), 16),
        b: parseInt(n.slice(5, 7), 16),
    };
}
export function rgbToHex({ r, g, b }) {
    const part = (v) => Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0');
    return `#${part(r)}${part(g)}${part(b)}`;
}
/** Hue (0–360), saturation and lightness (0–1) — what "is this green" is asked of. */
export function hexToHsl(hex) {
    const { r, g, b } = hexToRgb(hex);
    const [rn, gn, bn] = [r / 255, g / 255, b / 255];
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min)
        return { h: 0, s: 0, l };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rn)
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
    else if (max === gn)
        h = ((bn - rn) / d + 2) * 60;
    else
        h = ((rn - gn) / d + 4) * 60;
    return { h, s, l };
}
/** How far apart two colours look, roughly (0 the same, about 380 black to white). */
export function colorDistance(a, b) {
    const x = hexToRgb(a);
    const y = hexToRgb(b);
    // Weighted toward green, which the eye tells apart best.
    return Math.sqrt(2 * (x.r - y.r) ** 2 + 4 * (x.g - y.g) ** 2 + 3 * (x.b - y.b) ** 2) / 2;
}
/** Foliage: a green or olive with some colour in it. */
export function isFoliage(hex) {
    const { h, s, l } = hexToHsl(hex);
    return h >= 65 && h <= 170 && s >= 0.12 && l >= 0.12 && l <= 0.75;
}
// ─── Reading what is stored ─────────────────────────────────────────────────
function oneOf(list, value) {
    return typeof value === 'string' && list.includes(value)
        ? value
        : undefined;
}
function colorList(value, max) {
    if (!Array.isArray(value))
        return undefined;
    const colors = value.map(normalizeHexColor).filter((c) => c !== null);
    return colors.length ? [...new Set(colors)].slice(0, max) : undefined;
}
/**
 * A look read from stored JSON, keeping only what is well-formed — a field
 * somebody's old client wrote wrong is dropped, never a reason the 3D fails.
 */
export function readDecorLook(value, withPicks = true) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {};
    const v = value;
    const look = {};
    const color = (key) => {
        const c = normalizeHexColor(v[key]);
        if (c)
            look[key] = c;
    };
    [
        'linenColor',
        'napkinColor',
        'chairColor',
        'cushionColor',
        'greeneryColor',
        'chargerColor',
        'plateColor',
        'glassColor',
        'wallColor',
        'floorColor',
        'cocktailColor',
        'barColor',
        'chuppahFabricColor',
        'loungeColor',
    ].forEach(color);
    const choice = (key, list) => {
        const found = oneOf(list, v[key]);
        if (found)
            look[key] = found;
    };
    choice('linenFinish', LINEN_FINISHES);
    choice('napkinFold', NAPKIN_FOLDS);
    choice('glassShape', GLASS_SHAPES);
    choice('candleStyle', CANDLE_STYLES);
    choice('candleFinish', CANDLE_FINISHES);
    choice('cocktailStyle', COCKTAIL_TABLE_STYLES);
    choice('barStyle', BAR_STYLES);
    choice('danceFloorStyle', DANCE_FLOOR_STYLES);
    choice('chuppahStyle', CHUPPAH_STYLES);
    choice('loungeStyle', LOUNGE_STYLES);
    const chuppahFlowers = colorList(v.chuppahFlowerColors, MAX_FLOWER_COLORS);
    if (chuppahFlowers)
        look.chuppahFlowerColors = chuppahFlowers;
    if (withPicks) {
        const picks = readDecorPicks(v.picks);
        if (picks)
            look.picks = picks;
    }
    const chairStyle = oneOf(CHAIR_STYLES, v.chairStyle);
    if (chairStyle)
        look.chairStyle = chairStyle;
    const centerpiece = oneOf(CENTERPIECE_STYLES, v.centerpiece);
    if (centerpiece)
        look.centerpiece = centerpiece;
    const cutlery = oneOf(CUTLERY_FINISHES, v.cutlery);
    if (cutlery)
        look.cutlery = cutlery;
    const glass = oneOf(GLASS_STYLES, v.glass);
    if (glass)
        look.glass = glass;
    const floorFinish = oneOf(DECOR_FLOOR_FINISHES, v.floorFinish);
    if (floorFinish)
        look.floorFinish = floorFinish;
    if (typeof v.charger === 'boolean')
        look.charger = v.charger;
    const flowers = colorList(v.flowerColors, MAX_FLOWER_COLORS);
    if (flowers)
        look.flowerColors = flowers;
    return look;
}
/** The longest name a pick keeps. */
const MAX_PICK_NAME = 200;
/** Picks read from stored JSON — each a library id, a name and a look (never nested picks). */
export function readDecorPicks(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const picks = {};
    for (const [category, raw] of Object.entries(value)) {
        if (!raw || typeof raw !== 'object')
            continue;
        const r = raw;
        if (typeof r.itemId !== 'string' || !r.itemId || r.itemId.length > 64)
            continue;
        const pick = {
            itemId: r.itemId,
            name: typeof r.name === 'string' ? r.name.slice(0, MAX_PICK_NAME) : '',
            look: readDecorLook(r.look, false),
        };
        picks[category] = pick;
    }
    return Object.keys(picks).length ? picks : undefined;
}
/**
 * A level's look with its picks laid under it: each pick's fields, then the
 * fields set beside them (which win). What `resolveDecor` reads per level.
 */
export function expandLook(look) {
    if (!look)
        return {};
    const out = {};
    Object.values(look.picks ?? {}).forEach((pick) => {
        if (pick)
            Object.assign(out, readDecorLook(pick.look, false));
    });
    const own = { ...look };
    delete own.picks;
    return Object.assign(out, own);
}
/** How many colours a group of pictures keeps. */
export const MAX_PALETTE_COLORS = 8;
export function readDecorPalette(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {};
    const v = value;
    const palette = {};
    for (const key of ['chairs', 'flowers', 'tableware']) {
        const list = colorList(v[key], MAX_PALETTE_COLORS);
        if (list)
            palette[key] = list;
    }
    const wall = normalizeHexColor(v.hallWall);
    if (wall)
        palette.hallWall = wall;
    const floor = normalizeHexColor(v.hallFloor);
    if (floor)
        palette.hallFloor = floor;
    return palette;
}
/** An event's stored decor, or the empty one. */
export function readEventDecor(value) {
    const v = value && typeof value === 'object' ? value : {};
    return { look: readDecorLook(v.look), palette: readDecorPalette(v.palette) };
}
// ─── What the pictures say ──────────────────────────────────────────────────
/**
 * The fields the pictures can answer, from their colours. Styles — the kind of
 * chair, how tall the flowers stand — are never guessed from a colour; the AI
 * reads those, or the producer chooses.
 */
export function lookFromPalette(palette) {
    const look = {};
    const chairs = palette.chairs ?? [];
    if (chairs[0]) {
        look.chairColor = chairs[0];
        const cushion = chairs.slice(1).find((c) => colorDistance(c, chairs[0]) > 60);
        if (cushion)
            look.cushionColor = cushion;
    }
    const flowers = palette.flowers ?? [];
    const greenery = flowers.find(isFoliage);
    if (greenery)
        look.greeneryColor = greenery;
    // Blooms: not the greenery, and not the shadows and backdrop a photo of
    // flowers is full of — no arrangement is near-black.
    const blooms = flowers
        .filter((c) => !isFoliage(c) && hexToHsl(c).l >= 0.2)
        .slice(0, MAX_FLOWER_COLORS);
    if (blooms.length)
        look.flowerColors = blooms;
    const tableware = palette.tableware ?? [];
    // The plate: the lightest colour, if it is light enough to be china.
    const byLight = [...tableware].sort((a, b) => hexToHsl(b).l - hexToHsl(a).l);
    const plate = byLight.find((c) => hexToHsl(c).l >= 0.72);
    if (plate)
        look.plateColor = plate;
    // The charger: the most coloured of the rest, if it stands apart from the plate.
    const charger = tableware
        .filter((c) => c !== plate && (!plate || colorDistance(c, plate) > 50))
        .sort((a, b) => hexToHsl(b).s - hexToHsl(a).s)[0];
    if (charger)
        look.chargerColor = charger;
    if (palette.hallWall)
        look.wallColor = palette.hallWall;
    if (palette.hallFloor)
        look.floorColor = palette.hallFloor;
    return look;
}
const FIELDS = Object.keys(DEFAULT_DECOR);
/**
 * The look in force on a plan: the plan's own choice, else the event's, else
 * what the event's pictures say, else the default — and where each came from.
 */
export function resolveDecor(eventDecor, planLook) {
    const event = expandLook(eventDecor?.look);
    const pictures = lookFromPalette(eventDecor?.palette ?? {});
    const plan = expandLook(planLook);
    const values = { ...DEFAULT_DECOR };
    const sources = {};
    for (const field of FIELDS) {
        const layers = [
            ['plan', plan],
            ['event', event],
            ['pictures', pictures],
        ];
        const hit = layers.find(([, look]) => look[field] !== undefined);
        if (hit) {
            values[field] = hit[1][field];
            sources[field] = hit[0];
        }
        else {
            sources[field] = 'default';
        }
    }
    return { values: values, sources };
}
/** A plan's override with only the fields that differ from the event — nothing kept that changes nothing. */
export function decorOverride(look) {
    const clean = readDecorLook(look);
    return Object.keys(clean).length ? clean : null;
}
/**
 * The look one object on a plan is drawn in: the plan's look, with the
 * object's own picks and fields (`config.decor`) over it. Pure.
 */
export function resolveItemDecor(planValues, itemDecor) {
    const own = expandLook(readDecorLook(itemDecor));
    if (Object.keys(own).length === 0)
        return planValues;
    return { ...planValues, ...own };
}
/** Whether an object carries a look of its own. */
export function hasItemDecor(itemDecor) {
    const look = readDecorLook(itemDecor);
    return Object.keys(look).length > 0;
}
// ─── The look in words ──────────────────────────────────────────────────────
/** Colours a decorator names, nearest of which names any colour. */
const NAMED_COLORS = [
    ['white', '#ffffff'],
    ['ivory', '#f7f3e8'],
    ['champagne', '#e8d8b8'],
    ['blush pink', '#efc9c4'],
    ['dusty rose', '#c48b8f'],
    ['pink', '#e8a0b4'],
    ['burgundy', '#7a1f2b'],
    ['red', '#c0262e'],
    ['coral', '#ec7e66'],
    ['peach', '#f6c6a4'],
    ['orange', '#e3802d'],
    ['yellow', '#ecd14a'],
    ['gold', '#c6a052'],
    ['tan', '#c9ab84'],
    ['brown', '#6b4b33'],
    ['walnut', '#4a3324'],
    ['sage green', '#8fa47e'],
    ['olive green', '#6b7d55'],
    ['green', '#4f7a3e'],
    ['emerald', '#1f6b4a'],
    ['dusty blue', '#8ba6c1'],
    ['blue', '#3a64b0'],
    ['navy', '#2d3a5c'],
    ['lavender', '#b9a6d6'],
    ['purple', '#6a3d8f'],
    ['silver', '#c9c9c4'],
    ['grey', '#8a8a86'],
    ['charcoal', '#3c3c3a'],
    ['black', '#161616'],
];
/** A colour's everyday name — "navy", "blush pink" — for a prompt or a caption. */
export function colorName(hex) {
    let best = NAMED_COLORS[0];
    let bestDistance = Infinity;
    for (const named of NAMED_COLORS) {
        const d = colorDistance(hex, named[1]);
        if (d < bestDistance) {
            best = named;
            bestDistance = d;
        }
    }
    return best[0];
}
function namesOf(colors) {
    const names = [...new Set(colors.map(colorName))];
    return names.length <= 1
        ? (names[0] ?? 'white')
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
const CHAIR_WORDS = {
    roundback: 'round-back',
    chiavari: 'Chiavari',
    crossback: 'cross-back',
    ghost: 'clear acrylic ghost',
    banquet: 'upholstered banquet',
};
/**
 * The look as phrases a prompt can use — "navy floor-length linens", "gold
 * Chiavari chairs with ivory cushions" — so a picture drawn without the
 * realistic view (or a text-only fallback) still follows the producer's
 * choices. Pure.
 */
export function describeDecor(values, 
/** The plan's object types — the bar, dance floor, chuppah… are described only when there is one. */
objectTypes = []) {
    const phrases = [
        `${colorName(values.linenColor)} floor-length table linens`,
        `${colorName(values.napkinColor)} napkins`,
        values.chairStyle === 'ghost'
            ? `${colorName(values.chairColor)}-tinted clear acrylic ghost chairs`
            : `${colorName(values.chairColor)} ${CHAIR_WORDS[values.chairStyle]} chairs with ${colorName(values.cushionColor)} cushions`,
    ];
    const flowers = `${namesOf(values.flowerColors)} flowers with ${colorName(values.greeneryColor)} greenery`;
    phrases.push(values.centerpiece === 'low'
        ? `low centrepieces of ${flowers}`
        : values.centerpiece === 'tall'
            ? `tall centrepieces of ${flowers} on raised stands`
            : values.centerpiece === 'candles' && values.candleStyle !== 'none'
                ? 'candles on the tables, no flowers'
                : 'no centrepieces on the tables');
    phrases.push(values.charger
        ? `${colorName(values.chargerColor)} charger plates`
        : 'dinner plates without charger plates', `${colorName(values.plateColor)} dinner plates`, `${values.cutlery} cutlery`, values.glass === 'tinted'
        ? `${colorName(values.glassColor)} glassware`
        : values.glass === 'gold_rim'
            ? 'gold-rimmed glassware'
            : 'clear glassware');
    // The library's finer choices, said only when they are not the plain default.
    if (values.linenFinish && values.linenFinish !== 'plain') {
        phrases.push(`${values.linenFinish} table linens`);
    }
    if (values.napkinFold === 'standing')
        phrases.push('napkins folded standing on the plates');
    if (values.napkinFold === 'ring')
        phrases.push('napkins rolled in napkin rings');
    if (values.glassShape === 'goblet')
        phrases.push('goblet glassware');
    if (values.glassShape === 'stemless')
        phrases.push('stemless glassware');
    if (values.candleStyle === 'candelabra')
        phrases.push('tall candelabras on the tables');
    if (values.candleStyle === 'votives')
        phrases.push('votive candles on the tables');
    // The 3D view draws no candle at all then — not even round an arrangement.
    if (values.candleStyle === 'none' && values.centerpiece !== 'none') {
        phrases.push('no candles on the tables');
    }
    if (values.wallColor)
        phrases.push(`${colorName(values.wallColor)} walls`);
    phrases.push(values.floorColor
        ? `${colorName(values.floorColor)} ${values.floorFinish} floor`
        : `${values.floorFinish} floor`);
    phrases.push(...describeRoomPieces(values, new Set(objectTypes)));
    return phrases;
}
const BAR_WORDS = {
    wood: 'wooden',
    white: 'white lacquer',
    marble: 'marble-fronted',
    mirror: 'mirrored',
    led: 'illuminated LED',
};
const DANCE_FLOOR_WORDS = {
    checker: 'black-and-white checkered dance floor',
    white_gloss: 'glossy white dance floor',
    black_gloss: 'glossy black dance floor',
    wood: 'wooden parquet dance floor',
    led: 'illuminated LED dance floor',
};
/** The room's other pieces in words, for the ones the plan has. Pure. */
export function describeRoomPieces(values, types) {
    const phrases = [];
    if (types.has('COCKTAIL_TABLE')) {
        const cover = colorName(values.cocktailColor ?? values.linenColor);
        phrases.push(values.cocktailStyle === 'spandex'
            ? `${cover} spandex-covered cocktail tables`
            : values.cocktailStyle === 'draped'
                ? `cocktail tables draped in ${cover} and tied at the waist`
                : 'bare cocktail tables');
    }
    if (types.has('BAR')) {
        const tint = values.barColor ? ` in ${colorName(values.barColor)}` : '';
        phrases.push(`a ${BAR_WORDS[values.barStyle]} bar${tint}`);
    }
    if (types.has('DANCE_FLOOR'))
        phrases.push(`a ${DANCE_FLOOR_WORDS[values.danceFloorStyle]}`);
    if (types.has('CHUPPAH') || types.has('ARCH')) {
        const fabric = colorName(values.chuppahFabricColor);
        const flowers = namesOf(values.chuppahFlowerColors.length ? values.chuppahFlowerColors : values.flowerColors);
        phrases.push(values.chuppahStyle === 'floral'
            ? `a chuppah with a canopy of ${fabric} fabric and ${flowers} flowers massed on its corners`
            : values.chuppahStyle === 'draped'
                ? `a chuppah draped in ${fabric} fabric`
                : values.chuppahStyle === 'birch'
                    ? 'a birch-wood chuppah with greenery'
                    : `a slim modern-frame chuppah with a spray of ${flowers} flowers`);
    }
    if (['SOFA', 'LOVESEAT', 'ARMCHAIR', 'OTTOMAN'].some((t) => types.has(t))) {
        phrases.push(`${colorName(values.loungeColor)} ${values.loungeStyle} lounge furniture`);
    }
    return phrases;
}
//# sourceMappingURL=decor.js.map